// Data hooks for the pricing workspace's Excel/BOQ attachment panel and the
// column-mapping panel. All three endpoints live under /sales/requests and
// ride the same session cookie as the rest of admin/ (shared/api/client.js).
//
// The buyer no longer maps spreadsheet columns (PROGRESS.md entry 58) — a
// salesperson does it here, against the raw rows shown beside the line table.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

const uploadsKey = (requestId) => ['sales', 'requests', requestId, 'uploads'];
const gridKey = (requestId, uploadId, candidate) => [
  'sales',
  'requests',
  requestId,
  'uploads',
  uploadId,
  'grid',
  candidate || {},
];
const workspaceKey = (requestId) => ['sales', 'workspace', requestId];

// GET /sales/requests/:requestId/uploads — the buyer's attached files.
// Polls every 1.5s while any upload is 'mapped' (the worker is creating
// lines from a confirmed mapping); stops once nothing is mid-flight.
export function useRequestUploads(requestId) {
  return useQuery({
    queryKey: uploadsKey(requestId),
    queryFn: () => apiClient.get(`/sales/requests/${requestId}/uploads`),
    enabled: Boolean(requestId),
    select: (data) => data.uploads,
    refetchInterval: (query) => {
      const uploads = query.state.data?.uploads;
      const anyMapping = Array.isArray(uploads) && uploads.some((u) => u.parseStatus === 'mapped');
      return anyMapping ? 1500 : false;
    },
  });
}

// GET /sales/requests/:requestId/uploads/:uploadId/grid — the raw sheet rows
// plus, once the candidate names a description + quantity column, exactly
// what each data row would become. `candidate` is the salesperson's
// in-progress picks (1-based ints), sent as query params. Keeps the previous
// grid on screen while refetching so changing one column doesn't flash the
// whole table to a spinner.
export function useUploadGrid(requestId, uploadId, candidate, { enabled = true } = {}) {
  return useQuery({
    queryKey: gridKey(requestId, uploadId, candidate),
    queryFn: () =>
      apiClient.get(`/sales/requests/${requestId}/uploads/${uploadId}/grid`, candidate || {}),
    enabled: Boolean(requestId && uploadId && enabled),
    select: (data) => data.grid,
    placeholderData: (prev) => prev,
  });
}

// POST /sales/requests/:requestId/uploads/:uploadId/mapping — persists the
// confirmed mapping, flips the upload to 'mapped' and enqueues line creation
// in the worker. Invalidate the workspace (so it re-reads lines once the
// worker finishes) and the uploads list (so it starts polling).
export function useConfirmUploadMapping(requestId, uploadId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiClient.post(`/sales/requests/${requestId}/uploads/${uploadId}/mapping`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKey(requestId) });
      queryClient.invalidateQueries({ queryKey: uploadsKey(requestId) });
    },
  });
}

export default useRequestUploads;
